import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Mail, Trash2, ShieldCheck, Edit3, Copy, AlertTriangle, Activity, KeyRound, ChevronRight, CheckCircle2, XCircle, Settings2, Shield, AlertOctagon } from "lucide-react";
import Modal, { Field, Input, Select, GoldButton, GhostButton, PageHeader, EmptyState, StatusBadge } from "@/components/Ui";
import { SortableTh, useTableControls, FilterChip } from "@/components/SortableTable";
import { fmtDateTime } from "@/lib/time";

/**
 * Admin → Platform Team
 *
 * Manage platform staff (sales, support, billing, etc) — invite via magic-link,
 * configure their permissions, pin them to specific tenants, revoke access.
 *
 * Three sub-views:
 *   1. Employees list (default)
 *   2. Invite drawer
 *   3. Edit-permissions drawer (per-employee)
 */
export default function AdminPlatformTeam() {
    const [employees, setEmployees] = useState([]);
    const [roles, setRoles] = useState([]);
    const [catalog, setCatalog] = useState({ modules: [], default_actions: [], granularities: [] });
    const [tenants, setTenants] = useState([]);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [inviteLink, setInviteLink] = useState("");
    const [anomalyRulesOpen, setAnomalyRulesOpen] = useState(false);

    const load = useCallback(async () => {
        try {
            const [emps, rls, cat, tns] = await Promise.all([
                api.get("/platform/employees"),
                api.get("/platform/roles"),
                api.get("/platform/perms-catalog"),
                api.get("/tenants"),
            ]);
            setEmployees(emps.data);
            setRoles(rls.data);
            setCatalog(cat.data);
            setTenants(tns.data);
        } catch (e) { toast.error(apiError(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const revoke = async (emp) => {
        if (!window.confirm(`Revoke access for ${emp.email}? Active sessions are killed.`)) return;
        try { await api.delete(`/platform/employees/${emp.id}`); toast.success("Revoked"); load(); }
        catch (e) { toast.error(apiError(e)); }
    };
    const resend = async (emp) => {
        try {
            const { data } = await api.post(`/platform/employees/${emp.id}/resend-invite`);
            setInviteLink(data.invite_link);
            toast.success("New invite sent");
        } catch (e) { toast.error(apiError(e)); }
    };

    const { sort, setSort, rows: sortedEmployees, ...ctrlExtra } = useTableControls(employees, { initialSort: { key: "created_at", dir: "desc" } });
    const tableCtrl = { sort, setSort, rows: sortedEmployees, ...ctrlExtra };

    return (
        <>
            <PageHeader
                title="Platform Team"
                subtitle="Sales · Support · Billing · Compliance — every employee, every permission."
                action={
                    <div className="flex gap-2">
                        <GhostButton onClick={() => setAnomalyRulesOpen(true)} data-testid="anomaly-rules-btn">
                            <Settings2 className="w-4 h-4 inline mr-2" strokeWidth={2}/>Anomaly rules
                        </GhostButton>
                        <GoldButton onClick={() => setInviteOpen(true)} data-testid="invite-employee-btn"><Plus className="w-4 h-4 inline mr-2" strokeWidth={2}/>Invite employee</GoldButton>
                    </div>
                }
            />

            {inviteLink && (
                <div className="border border-emerald-700/40 bg-emerald-950/30 p-4 mb-6 flex items-start gap-3 text-xs">
                    <Mail className="w-4 h-4 text-emerald-400 mt-0.5" strokeWidth={1.5}/>
                    <div className="flex-1">
                        <div className="text-emerald-300 font-medium mb-1">Invite link generated.</div>
                        <div className="text-muted-foreground mb-2">Email sent. If they don&apos;t receive it, share this link manually (valid 48h):</div>
                        <div className="flex items-center gap-2">
                            <code className="bg-background border border-border px-2 py-1 truncate flex-1 text-[10px]" data-testid="invite-link">{inviteLink}</code>
                            <button onClick={() => { navigator.clipboard.writeText(inviteLink); toast.success("Copied"); }} data-testid="invite-link-copy" className="text-emerald-400 hover:text-emerald-300"><Copy className="w-3.5 h-3.5"/></button>
                            <button onClick={() => setInviteLink("")} className="text-muted-foreground hover:text-foreground text-xs">Dismiss</button>
                        </div>
                    </div>
                </div>
            )}

            {employees.length === 0 ? (
                <EmptyState title="No employees yet" subtitle="Invite your first platform team member to get started." action={<GoldButton onClick={() => setInviteOpen(true)} data-testid="employees-empty-add">Invite</GoldButton>}/>
            ) : (
                <div className="border border-border overflow-x-auto">
                    <table className="min-w-[800px] w-full text-sm" data-testid="platform-team-table">
                        <thead className="bg-muted border-b border-border">
                            <tr className="text-left">
                                <SortableTh ctrl={tableCtrl} field="name" testIdPrefix="sort-emp">Name</SortableTh>
                                <SortableTh ctrl={tableCtrl} field="email" testIdPrefix="sort-emp">Email</SortableTh>
                                <SortableTh ctrl={tableCtrl} field="role" testIdPrefix="sort-emp">Role</SortableTh>
                                <th className="px-4 py-3 text-xs uppercase font-medium tracking-wider">Scope</th>
                                <SortableTh ctrl={tableCtrl} field="is_active" testIdPrefix="sort-emp">Status</SortableTh>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedEmployees.map(e => {
                                const role = roles.find(r => r.key === e.role);
                                const scopeLabel = e.tenant_scope === "all" || !e.tenant_scope
                                    ? "All tenants"
                                    : `${(e.tenant_scope?.pinned || []).length} pinned`;
                                const status = e.deleted_at ? "deleted" : e.invite_pending ? "invited" : e.is_active ? "active" : "inactive";
                                return (
                                    <tr key={e.id} className="border-b border-border hover:bg-muted/50" data-testid={`employee-row-${e.id}`}>
                                        <td className="px-4 py-3">{e.name}</td>
                                        <td className="px-4 py-3 text-xs text-muted-foreground">{e.email}</td>
                                        <td className="px-4 py-3 text-xs">{role?.label || e.role}</td>
                                        <td className="px-4 py-3 text-xs text-muted-foreground">{scopeLabel}</td>
                                        <td className="px-4 py-3"><StatusBadge status={status}/></td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="inline-flex gap-3 items-center">
                                                <button onClick={() => setEditTarget(e)} data-testid={`employee-edit-${e.id}`} className="text-xs text-primary hover:underline inline-flex items-center gap-1"><Edit3 className="w-3 h-3" strokeWidth={1.5}/>Edit</button>
                                                {e.invite_pending && (
                                                    <button onClick={() => resend(e)} data-testid={`employee-resend-${e.id}`} className="text-xs text-emerald-400 hover:underline inline-flex items-center gap-1"><Mail className="w-3 h-3" strokeWidth={1.5}/>Resend</button>
                                                )}
                                                <button onClick={() => revoke(e)} data-testid={`employee-revoke-${e.id}`} className="text-xs text-red-400 hover:underline inline-flex items-center gap-1"><Trash2 className="w-3 h-3" strokeWidth={1.5}/>Revoke</button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <InviteModal open={inviteOpen} onClose={() => setInviteOpen(false)} roles={roles} tenants={tenants} catalog={catalog} onCreated={(link) => { setInviteLink(link); load(); }}/>
            {editTarget && (
                <EditEmployeeModal employee={editTarget} roles={roles} tenants={tenants} catalog={catalog}
                    onClose={() => setEditTarget(null)} onSaved={() => { setEditTarget(null); load(); }}/>
            )}
            {anomalyRulesOpen && <AnomalyRulesModal onClose={() => setAnomalyRulesOpen(false)}/>}
        </>
    );
}


function InviteModal({ open, onClose, roles, tenants, catalog, onCreated }) {
    const [form, setForm] = useState({ email: "", name: "", role: "", scope_mode: "all", pinned: [] });
    const [saving, setSaving] = useState(false);
    useEffect(() => { if (!open) { setForm({ email: "", name: "", role: "", scope_mode: "all", pinned: [] }); setSaving(false); } }, [open]);

    const submit = async (e) => {
        e.preventDefault(); setSaving(true);
        try {
            const payload = {
                email: form.email.trim().toLowerCase(),
                name: form.name.trim(),
                role: form.role,
                tenant_scope: form.scope_mode === "all" ? "all" : { pinned: form.pinned },
            };
            const { data } = await api.post("/platform/employees/invite", payload);
            toast.success("Invite sent");
            onCreated(data.invite_link);
            onClose();
        } catch (err) { toast.error(apiError(err)); }
        finally { setSaving(false); }
    };

    return (
        <Modal open={open} onClose={onClose} title="Invite employee" size="lg" testId="invite-modal">
            <form onSubmit={submit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Full name"><Input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="invite-name"/></Field>
                    <Field label="Work email"><Input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="invite-email"/></Field>
                </div>
                <Field label="Role">
                    <Select required value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}
                        options={[{ value: "", label: "— select role —" }, ...roles.filter(r => r.key !== "super_admin").map(r => ({ value: r.key, label: r.label }))]}
                        data-testid="invite-role"/>
                </Field>
                {form.role && (
                    <div className="border border-border p-3 bg-background text-xs text-muted-foreground">
                        {roles.find(r => r.key === form.role)?.description}
                    </div>
                )}
                <Field label="Tenant access">
                    <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input type="radio" checked={form.scope_mode === "all"} onChange={() => setForm({ ...form, scope_mode: "all" })} data-testid="invite-scope-all"/>
                            <span>All tenants (full access)</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input type="radio" checked={form.scope_mode === "pinned"} onChange={() => setForm({ ...form, scope_mode: "pinned" })} data-testid="invite-scope-pinned"/>
                            <span>Pin to specific tenants</span>
                        </label>
                    </div>
                </Field>
                {form.scope_mode === "pinned" && (
                    <Field label="Select tenants">
                        <div className="max-h-48 overflow-y-auto border border-border p-3 space-y-1" data-testid="invite-pinned-list">
                            {tenants.map(t => (
                                <label key={t.id} className="flex items-center gap-2 text-sm">
                                    <input type="checkbox" checked={form.pinned.includes(t.id)}
                                        onChange={(e) => {
                                            const next = e.target.checked ? [...form.pinned, t.id] : form.pinned.filter(x => x !== t.id);
                                            setForm({ ...form, pinned: next });
                                        }}/>
                                    <span>{t.name}</span><span className="text-muted-foreground text-xs ml-auto">{t.slug}</span>
                                </label>
                            ))}
                        </div>
                    </Field>
                )}
                <div className="flex items-start gap-2 text-xs text-muted-foreground bg-background border border-border p-3">
                    <ShieldCheck className="w-4 h-4 text-primary mt-0.5 shrink-0"/>
                    <div>The employee will get a magic-link valid for 48h. They&apos;ll set their password and enroll a TOTP authenticator (Google Authenticator / Authy) before their account activates.</div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <GhostButton type="button" onClick={onClose}>Cancel</GhostButton>
                    <GoldButton disabled={saving} data-testid="invite-submit">{saving ? "Sending…" : "Send invite"}</GoldButton>
                </div>
            </form>
        </Modal>
    );
}


function EditEmployeeModal({ employee, roles, tenants, catalog, onClose, onSaved }) {
    const [tab, setTab] = useState("perms");   // perms | activity
    const role = useMemo(() => roles.find(r => r.key === employee.role), [roles, employee.role]);
    const baseModules = role?.perms?.modules || {};
    const [overrides, setOverrides] = useState(employee.perm_overrides || {});
    const [scopeMode, setScopeMode] = useState(employee.tenant_scope === "all" || !employee.tenant_scope ? "all" : "pinned");
    const [pinned, setPinned] = useState(employee.tenant_scope?.pinned || []);
    const [granularity, setGranularity] = useState(overrides.granularity || role?.default_granularity || "module");
    const [newRole, setNewRole] = useState(employee.role);
    const [saving, setSaving] = useState(false);

    const effectiveModule = (modKey) => {
        const base = baseModules[modKey] || {};
        const ovr = (overrides.modules || {})[modKey] || {};
        return { ...base, ...ovr };
    };

    const setModuleAction = (modKey, action, value) => {
        setOverrides(prev => ({
            ...prev,
            modules: { ...(prev.modules || {}), [modKey]: { ...((prev.modules || {})[modKey] || {}), [action]: value } },
        }));
    };

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                role: newRole,
                tenant_scope: scopeMode === "all" ? "all" : { pinned },
                perm_overrides: { ...overrides, granularity },
            };
            await api.patch(`/platform/employees/${employee.id}`, payload);
            toast.success("Saved");
            onSaved();
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };

    // Group modules by category
    const groups = useMemo(() => {
        const out = {};
        for (const m of catalog.modules || []) {
            out[m.category] = out[m.category] || [];
            out[m.category].push(m);
        }
        return out;
    }, [catalog.modules]);

    return (
        <Modal open={true} onClose={onClose} title={`Edit · ${employee.name}`} size="xl" testId="edit-employee-modal">
            <div className="border-b border-border -mt-2 -mx-6 px-6 mb-5">
                <div className="flex gap-1">
                    <button
                        onClick={() => setTab("perms")}
                        data-testid="tab-perms"
                        className={`px-4 py-2.5 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2 border-b-2 ${tab === "perms" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                        <KeyRound className="w-3.5 h-3.5" strokeWidth={1.5}/>Permissions
                    </button>
                    <button
                        onClick={() => setTab("activity")}
                        data-testid="tab-activity"
                        className={`px-4 py-2.5 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2 border-b-2 ${tab === "activity" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                        <Activity className="w-3.5 h-3.5" strokeWidth={1.5}/>Activity log
                    </button>
                </div>
            </div>

            {tab === "activity" ? (
                <EmployeeActivityLog employee={employee} onClose={onClose}/>
            ) : (
            <div className="space-y-5">
                <div className="grid sm:grid-cols-3 gap-4">
                    <Field label="Role">
                        <Select value={newRole} onChange={e => setNewRole(e.target.value)}
                            options={roles.filter(r => r.key !== "super_admin").map(r => ({ value: r.key, label: r.label }))}
                            data-testid="edit-role"/>
                    </Field>
                    <Field label="Permission granularity" hint="Field & column level apply progressively.">
                        <Select value={granularity} onChange={e => setGranularity(e.target.value)}
                            options={catalog.granularities.map(g => ({ value: g.key, label: g.label }))}
                            data-testid="edit-granularity"/>
                    </Field>
                    <Field label="Tenant scope">
                        <Select value={scopeMode} onChange={e => setScopeMode(e.target.value)}
                            options={[{ value: "all", label: "All tenants" }, { value: "pinned", label: "Pinned tenants only" }]}
                            data-testid="edit-scope-mode"/>
                    </Field>
                </div>

                {scopeMode === "pinned" && (
                    <div className="border border-border p-3 max-h-40 overflow-y-auto space-y-1" data-testid="edit-pinned-list">
                        {tenants.map(t => (
                            <label key={t.id} className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={pinned.includes(t.id)}
                                    onChange={(e) => setPinned(p => e.target.checked ? [...p, t.id] : p.filter(x => x !== t.id))}/>
                                <span>{t.name}</span><span className="text-muted-foreground text-xs ml-auto">{t.slug}</span>
                            </label>
                        ))}
                    </div>
                )}

                <div>
                    <div className="overline mb-3">Module permissions</div>
                    <div className="space-y-4">
                        {Object.entries(groups).map(([cat, mods]) => (
                            <div key={cat} className="border border-border">
                                <div className="bg-muted px-4 py-2 text-xs uppercase tracking-wider text-muted-foreground border-b border-border">{cat}</div>
                                <div className="divide-y divide-border">
                                    {mods.map(m => {
                                        const actions = m.actions || catalog.default_actions;
                                        const eff = effectiveModule(m.key);
                                        return (
                                            <div key={m.key} className="px-4 py-2.5 flex items-center justify-between gap-3" data-testid={`module-row-${m.key}`}>
                                                <div className="text-sm flex-1">{m.label}</div>
                                                <div className="flex gap-3">
                                                    {actions.map(a => (
                                                        <label key={a} className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                                                            <input type="checkbox" checked={!!eff[a]}
                                                                onChange={(e) => setModuleAction(m.key, a, e.target.checked)}
                                                                data-testid={`perm-${m.key}-${a}`}/>
                                                            {a}
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {(granularity === "module+field" || granularity === "module+field+column") && (
                    <div className="border border-amber-700/40 bg-amber-950/20 p-3 text-xs text-amber-300 flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0"/>
                        <div>Field {granularity === "module+field+column" ? "& column " : ""}level controls are tracked in user permissions and emitted to the frontend for UI gating. Server-side enforcement currently operates at module-action level — server-side field/column gating is a Phase-2 deliverable.</div>
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-2 border-t border-border">
                    <GhostButton onClick={onClose}>Cancel</GhostButton>
                    <GoldButton disabled={saving} onClick={save} data-testid="edit-employee-save">{saving ? "Saving…" : "Save changes"}</GoldButton>
                </div>
            </div>
            )}
        </Modal>
    );
}


// ----------------------------------------------------------------------------
// Activity log tab — paginated unified timeline of logins + audit events
// ----------------------------------------------------------------------------
const EVENT_LABELS = {
    "auth.login_success": "Login (success)",
    "auth.login_failed": "Login (failed)",
    "login.success": "Login (success)",
    "login.failed": "Login (failed)",
    "platform.invite_accepted": "Accepted invite + enrolled TOTP",
    "tenant.soft_delete": "Deleted tenant",
    "tenant.bulk_soft_delete": "Bulk-deleted tenants",
    "tenant.restore": "Restored tenant",
    "tenant_user.soft_delete": "Deleted tenant user",
    "tenant_user.bulk_soft_delete": "Bulk-deleted tenant users",
    "accounting.period_closed": "Closed accounting period",
    "accounting.period_reopened": "Reopened accounting period",
};

function EmployeeActivityLog({ employee, onClose }) {
    const [data, setData] = useState(null);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [filterAnomaliesOnly, setFilterAnomaliesOnly] = useState(false);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        api.get(`/platform/employees/${employee.id}/activity`, { params: { page, limit: 50 } })
            .then(r => { if (alive) setData(r.data); })
            .catch(e => toast.error(apiError(e)))
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [employee.id, page]);

    const exportCsv = () => {
        if (!data?.items?.length) return;
        const headers = ["timestamp", "source", "event", "ip", "user_agent", "tenant_id", "success", "anomalies", "details"];
        const rows = data.items.map(it => [
            it.ts, it.source, it.event,
            it.ip || "", (it.user_agent || "").replace(/"/g, '""'),
            it.tenant_id || "",
            it.success === true ? "yes" : it.success === false ? "no" : "",
            (it.anomalies || []).map(a => `${a.rule}:${a.severity}`).join("|"),
            JSON.stringify(it.details || {}).replace(/"/g, '""'),
        ]);
        const csv = [headers.join(","), ...rows.map(r => r.map(c => `"${c}"`).join(","))].join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `activity-${employee.email}-${Date.now()}.csv`;
        a.click(); URL.revokeObjectURL(url);
    };

    const sevStyles = (s) => ({
        high:   { border: "border-red-700/50",   bg: "bg-red-950/20",   text: "text-red-400",   icon: AlertOctagon },
        medium: { border: "border-amber-700/40", bg: "bg-amber-950/20", text: "text-amber-300", icon: AlertTriangle },
        low:    { border: "border-blue-700/40",  bg: "bg-blue-950/15",  text: "text-blue-300",  icon: Shield },
    }[s] || { border: "", bg: "", text: "text-muted-foreground", icon: Shield });

    const rowMaxSev = (it) => {
        const sevs = (it.anomalies || []).map(a => a.severity);
        if (sevs.includes("high")) return "high";
        if (sevs.includes("medium")) return "medium";
        if (sevs.includes("low")) return "low";
        return null;
    };

    const visibleItems = filterAnomaliesOnly
        ? (data?.items || []).filter(it => (it.anomalies || []).length > 0)
        : (data?.items || []);

    return (
        <div className="space-y-4" data-testid="activity-log-panel">
            <div className="flex items-center justify-between text-xs flex-wrap gap-2">
                <div className="text-muted-foreground">
                    {data ? <>Showing <span className="text-foreground">{visibleItems.length}</span> of <span className="text-foreground">{data.total}</span> events</> : "Loading…"}
                </div>
                {data?.anomalies?.total > 0 && (
                    <div className="inline-flex items-center gap-2" data-testid="anomaly-summary">
                        {data.anomalies.high > 0 && <span className="px-2 py-1 border border-red-700/50 bg-red-950/30 text-red-300 text-[10px] uppercase tracking-[0.1em]">{data.anomalies.high} High</span>}
                        {data.anomalies.medium > 0 && <span className="px-2 py-1 border border-amber-700/40 bg-amber-950/20 text-amber-300 text-[10px] uppercase tracking-[0.1em]">{data.anomalies.medium} Medium</span>}
                        {data.anomalies.low > 0 && <span className="px-2 py-1 border border-blue-700/40 bg-blue-950/15 text-blue-300 text-[10px] uppercase tracking-[0.1em]">{data.anomalies.low} Low</span>}
                        <label className="inline-flex items-center gap-1 cursor-pointer text-muted-foreground hover:text-foreground ml-1" data-testid="filter-anomalies-only">
                            <input type="checkbox" checked={filterAnomaliesOnly} onChange={e => setFilterAnomaliesOnly(e.target.checked)}/>
                            Anomalies only
                        </label>
                    </div>
                )}
                <button onClick={exportCsv} disabled={!data?.items?.length}
                    data-testid="activity-export-csv"
                    className="px-3 py-1.5 border border-primary/40 text-primary hover:bg-primary/10 text-[10px] uppercase tracking-[0.15em] disabled:opacity-40">
                    Export CSV
                </button>
            </div>

            <div className="border border-border max-h-[60vh] overflow-y-auto" data-testid="activity-log-list">
                {loading && <div className="text-center py-12 text-muted-foreground text-xs">Loading activity…</div>}
                {!loading && visibleItems.length === 0 && (
                    <div className="text-center py-12 text-muted-foreground text-xs">{filterAnomaliesOnly ? "No anomalies on this page." : "No activity recorded yet for this employee."}</div>
                )}
                {!loading && visibleItems.map((it, idx) => {
                    const label = EVENT_LABELS[it.event] || it.event.replace(/[._]/g, " ");
                    const isAuth = it.source === "login" || it.event.startsWith("auth.") || it.event.startsWith("login.");
                    const isFail = it.event.endsWith(".failed") || it.success === false;
                    const sev = rowMaxSev(it);
                    const style = sev ? sevStyles(sev) : null;
                    return (
                        <div key={idx} className={`px-4 py-3 border-b border-border last:border-b-0 hover:bg-muted/40 flex items-start gap-3 text-xs ${style ? `border-l-2 ${style.border} ${style.bg}` : ""}`} data-testid={`activity-row-${idx}`}>
                            <div className={`mt-0.5 ${isFail ? "text-red-400" : isAuth ? "text-emerald-400" : "text-primary"}`}>
                                {isFail ? <XCircle className="w-3.5 h-3.5" strokeWidth={1.5}/> : isAuth ? <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.5}/> : <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.5}/>}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-foreground capitalize">{label}</span>
                                    {(it.anomalies || []).map((a, i) => {
                                        const s = sevStyles(a.severity);
                                        const Icon = s.icon;
                                        return (
                                            <span key={i}
                                                title={a.reason}
                                                data-testid={`anomaly-badge-${a.rule}`}
                                                className={`inline-flex items-center gap-1 px-1.5 py-0.5 border text-[9px] uppercase tracking-wider ${s.border} ${s.bg} ${s.text}`}>
                                                <Icon className="w-2.5 h-2.5" strokeWidth={2}/>
                                                {a.label}
                                            </span>
                                        );
                                    })}
                                </div>
                                <div className="text-[10px] text-muted-foreground flex gap-3 flex-wrap mt-0.5">
                                    <span>{fmtDateTime(it.ts)}</span>
                                    {it.ip && <span>IP {it.ip}</span>}
                                    {it.tenant_id && <span>Tenant {it.tenant_id.slice(-6)}</span>}
                                    {it.device_fingerprint && <span>FP {String(it.device_fingerprint).slice(0, 8)}…</span>}
                                </div>
                                {(it.anomalies || []).length > 0 && (
                                    <div className="text-[10px] mt-1 space-y-0.5">
                                        {it.anomalies.map((a, i) => (
                                            <div key={i} className={sevStyles(a.severity).text}>↳ {a.reason}</div>
                                        ))}
                                    </div>
                                )}
                                {it.user_agent && (
                                    <div className="text-[10px] text-[#5A5A5A] mt-1 truncate">{it.user_agent}</div>
                                )}
                                {it.details && Object.keys(it.details).length > 0 && (
                                    <div className="text-[10px] text-[#7A7A7A] mt-1 font-mono break-all">
                                        {JSON.stringify(it.details)}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {data && data.total > data.limit && (
                <div className="flex justify-between items-center text-xs">
                    <GhostButton onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} data-testid="activity-prev">← Prev</GhostButton>
                    <span className="text-muted-foreground">Page {page} of {Math.ceil(data.total / data.limit)}</span>
                    <GhostButton onClick={() => setPage(p => p + 1)} disabled={page * data.limit >= data.total} data-testid="activity-next">Next →</GhostButton>
                </div>
            )}

            <div className="flex justify-end pt-2 border-t border-border">
                <GhostButton onClick={onClose}>Close</GhostButton>
            </div>
        </div>
    );
}


// ----------------------------------------------------------------------------
// Configure anomaly rules (super-admin)
// ----------------------------------------------------------------------------
function AnomalyRulesModal({ onClose }) {
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/platform/anomaly-rules");
            setRules(data);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const patch = async (key, body) => {
        try {
            await api.patch(`/platform/anomaly-rules/${key}`, body);
            toast.success("Rule updated");
            load();
        } catch (e) { toast.error(apiError(e)); }
    };

    const sevColor = (s) => ({ high: "text-red-400", medium: "text-amber-300", low: "text-blue-300" }[s] || "text-muted-foreground");

    return (
        <Modal open={true} onClose={onClose} title="Anomaly detection rules" size="lg" testId="anomaly-rules-modal">
            <div className="space-y-3">
                <p className="text-xs text-muted-foreground">Each enabled rule scans every event in the platform team&apos;s activity log. Tune thresholds or disable rules that produce noise.</p>
                {loading && <div className="text-center py-8 text-muted-foreground text-xs">Loading…</div>}
                {!loading && rules.map(r => (
                    <div key={r.key} className="border border-border p-4 space-y-2" data-testid={`anomaly-rule-${r.key}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-foreground">{r.label}</span>
                                    <span className={`text-[10px] uppercase tracking-[0.15em] ${sevColor(r.severity)}`}>{r.severity}</span>
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">{r.description}</div>
                            </div>
                            <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                                <input type="checkbox" checked={r.enabled}
                                    onChange={(e) => patch(r.key, { enabled: e.target.checked })}
                                    data-testid={`anomaly-rule-toggle-${r.key}`}/>
                                <span className={r.enabled ? "text-emerald-400" : "text-muted-foreground"}>{r.enabled ? "Enabled" : "Disabled"}</span>
                            </label>
                        </div>
                        {r.config && Object.keys(r.config).length > 0 && (
                            <div className="border-t border-border pt-2 mt-2">
                                <div className="overline mb-2">Configuration</div>
                                <div className="grid sm:grid-cols-2 gap-2">
                                    {Object.entries(r.config).map(([k, v]) => (
                                        <div key={k} className="flex items-center gap-2">
                                            <label className="text-xs text-muted-foreground capitalize flex-1">{k.replace(/_/g, " ")}</label>
                                            <input
                                                type={typeof v === "number" ? "number" : "text"}
                                                defaultValue={v}
                                                onBlur={(e) => {
                                                    const newVal = typeof v === "number" ? Number(e.target.value) : e.target.value;
                                                    if (newVal !== v) patch(r.key, { config: { ...r.config, [k]: newVal } });
                                                }}
                                                data-testid={`anomaly-rule-cfg-${r.key}-${k}`}
                                                className="bg-background border border-border focus:border-primary outline-none px-2 py-1 text-xs w-32 text-right"/>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
                <div className="flex justify-end pt-2 border-t border-border">
                    <GhostButton onClick={onClose}>Close</GhostButton>
                </div>
            </div>
        </Modal>
    );
}
